/**
 * GatewayBirthPanel: what a gateway announced about itself, on the gateway
 * detail sheet (web brief U7 and section 5):
 *
 * - the birth: online / offline from the birth and its Last Will (not
 *   last_seen, which does not move on an idle gateway), firmware, host, config
 *   versions, drivers, serial ports and when it last announced itself;
 * - a read-only Networks panel from `capabilities.networks`: interfaces,
 *   addresses, router, which carries the internet, Wi-Fi SSID, up / down.
 *   Changing a gateway's network stays on the phone over Bluetooth (ADR 0002
 *   Amendment A9, with its confirm-or-revert window), since a remote change
 *   could cut the gateway off;
 * - which protocols a new device on this gateway may use, with the reason for
 *   the others (`GET /v1/things/drivers?gateway_id=`);
 * - "Change product", offered when the firmware runs more drivers than the
 *   gateway's product lets it use (a GatewayPro commissioned as a Modbus
 *   gateway), switching it to the product that covers them.
 *
 * A view over props (the sheet owns the queries and the PATCH), so it renders
 * in tests without a query client.
 *
 * `section` splits it across the gateway sheet's tabs: "overview" is the
 * connection and firmware on one line plus the networks folded to a summary
 * line; "protocols" is config versions, drivers, serial ports, the protocols
 * new devices may use and the product switch. "all" (default) is everything.
 */

import { formatDistance } from "date-fns";
import { Check, Globe, Loader2, Network, Server, Wifi } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Disclosure } from "@/lib/ui/disclosure";
import { cn } from "@/lib/ui/cn";
import type { DriverInfo, Thing, ThingProduct } from "@/lib/api";
import { driverLabel, driverStatusBadge, protocolChoices } from "@/lib/iot/drivers";
import { hasBirth, readBirth, speaksConfigV2 } from "@/lib/iot/gateway-birth";

export interface GatewayBirthPanelProps {
  thing: Thing;
  drivers: DriverInfo[] | undefined;
  driversLoading?: boolean;
  /** The gateway's current product, and the product to offer instead (see productSwitchCandidate). */
  product: ThingProduct | null;
  switchTo: ThingProduct | null;
  onSwitchProduct?: (productId: string) => void;
  switching?: boolean;
  switchError?: string | null;
  /** For relative times in tests. */
  now?: Date;
  /** Which part to show (the gateway sheet's tabs); default everything. */
  section?: "all" | "overview" | "protocols";
}

function ago(iso: string | null | undefined, now?: Date): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (!Number.isFinite(t.getTime())) return null;
  return formatDistance(t, now ?? new Date(), { addSuffix: true });
}

export function GatewayBirthPanel({
  thing,
  drivers,
  driversLoading,
  product,
  switchTo,
  onSwitchProduct,
  switching,
  switchError,
  now,
  section = "all",
}: GatewayBirthPanelProps) {
  const birth = readBirth(thing);
  const announced = hasBirth(thing);
  const at = ago(thing.capabilities_at, now);
  const changed = ago(thing.online_changed_at, now);
  const choices = protocolChoices(drivers);

  if (section === "overview") {
    return (
      <section aria-label="Gateway" className="flex flex-col gap-3 rounded-md border border-border bg-surface-elevated p-3">
        {!announced ? (
          <p className="text-[11px] text-on-surface-muted">
            This gateway has not announced its capabilities (firmware older than GatewayPro v0.3, VSync, Blackbox):
            it runs Modbus devices, and its status comes from when data last arrived.
          </p>
        ) : (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {thing.online === true ? (
              <span className="text-success">Online{changed ? ` since ${changed}` : ""}</span>
            ) : thing.online === false ? (
              <span className="text-danger">Offline{changed ? ` since ${changed}` : ""}</span>
            ) : (
              <span className="text-on-surface-muted">Connection unknown</span>
            )}
            {birth.fw && (
              <span className="font-mono text-on-surface">
                · fw {birth.fw}
                {birth.host ? <span className="text-on-surface-muted"> · {birth.host}</span> : null}
              </span>
            )}
            {speaksConfigV2(birth) && (
              <Badge variant="secondary" className="text-[10px]">
                multi-protocol
              </Badge>
            )}
          </p>
        )}
        {announced && (
          <Disclosure summary="Networks" hint={networksSummary(thing)} keepMounted>
            <NetworksPanel thing={thing} updated={at} />
          </Disclosure>
        )}
      </section>
    );
  }

  const showBirth = section === "all";
  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-elevated p-3">
      {showBirth && (
        <header className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          <Server className="size-3.5" /> Gateway
        </header>
      )}

      {!announced ? (
        <p className="text-[11px] text-on-surface-muted">
          This gateway has not announced its capabilities. Firmware older than GatewayPro v0.3 (and VSync
          and Blackbox boxes) never does: it runs Modbus devices, and its status comes from when data last
          arrived.
        </p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {showBirth && (
            <>
              <dt className="text-on-surface-muted">Connection</dt>
              <dd className="text-on-surface">
                {thing.online === true ? (
                  <span className="text-success">Online{changed ? ` since ${changed}` : ""}</span>
                ) : thing.online === false ? (
                  <span className="text-danger">Offline{changed ? ` since ${changed}` : ""}</span>
                ) : (
                  "Unknown"
                )}
              </dd>
            </>
          )}
          {showBirth && birth.fw && (
            <>
              <dt className="text-on-surface-muted">Firmware</dt>
              <dd className="font-mono text-on-surface">
                {birth.fw}
                {birth.host ? <span className="text-on-surface-muted"> · {birth.host}</span> : null}
              </dd>
            </>
          )}
          <dt className="text-on-surface-muted">Config</dt>
          <dd className="text-on-surface">
            {birth.configVersions.length > 0 ? `v${birth.configVersions.join(", v")}` : "—"}
            {speaksConfigV2(birth) ? <span className="text-on-surface-muted"> · multi-protocol</span> : null}
          </dd>
          <dt className="text-on-surface-muted">Drivers</dt>
          <dd className="flex flex-wrap gap-1">
            {birth.drivers.length === 0
              ? "—"
              : birth.drivers.map((d) => (
                  <Badge key={d.id} variant="secondary" className="text-[10px]">
                    {driverLabel(d.id, drivers)}
                    {d.v ? <span className="ml-1 text-on-surface-muted">v{d.v}</span> : null}
                  </Badge>
                ))}
          </dd>
          <dt className="text-on-surface-muted">Serial ports</dt>
          <dd className="font-mono text-on-surface">{birth.serialPorts.length > 0 ? birth.serialPorts.join(", ") : "none"}</dd>
          {at && (
            <>
              <dt className="text-on-surface-muted">Last announced</dt>
              <dd className="text-on-surface">{at}</dd>
            </>
          )}
        </dl>
      )}

      {announced && showBirth && <NetworksPanel thing={thing} updated={at} />}

      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Protocols for new devices
        </span>
        {driversLoading ? (
          <span className="flex items-center gap-1.5 text-[11px] text-on-surface-muted">
            <Loader2 className="size-3 animate-spin" /> Loading…
          </span>
        ) : choices.length === 0 ? (
          <span className="text-[11px] text-on-surface-muted">This server does not list protocols per gateway.</span>
        ) : (
          <ul className="flex flex-col gap-1">
            {choices.map((c) => (
              <li key={c.id} className="flex items-start gap-2 text-xs">
                <span className={cn("mt-0.5 size-3.5 shrink-0", c.available ? "text-success" : "text-on-surface-muted")}>
                  {c.available ? <Check className="size-3.5" /> : "–"}
                </span>
                <span className="flex flex-col">
                  <span className={c.available ? "text-on-surface" : "text-on-surface-muted"}>
                    {c.label}
                    {driverStatusBadge(c.status) && (
                      <span className="ml-1.5 text-[10px] text-on-surface-muted">{driverStatusBadge(c.status)}</span>
                    )}
                  </span>
                  {!c.available && c.reason && <span className="text-[11px] text-on-surface-muted">{c.reason}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {switchTo && (
        <div className="flex flex-col gap-1.5 rounded-md border border-brand/40 bg-brand-muted/20 p-2.5 text-[11px]">
          <span className="font-medium text-on-surface">Change product</span>
          <span className="text-on-surface-muted">
            This gateway&apos;s firmware runs {birth.drivers.map((d) => driverLabel(d.id, drivers)).join(", ")}, but it
            is set up as {product ? `a ${product.label}` : "a product"}, so only{" "}
            {(product?.drivers ?? []).map((d) => driverLabel(d, drivers)).join(", ") || "its own protocols"} can be added.
            Switch it to {switchTo.label} to add devices on the other protocols. Existing devices stay as they are.
          </span>
          {switchError && (
            <span role="alert" className="text-danger">
              {switchError}
            </span>
          )}
          {onSwitchProduct && (
            <Button size="sm" variant="outline" className="self-start" disabled={switching} onClick={() => onSwitchProduct(switchTo.id)}>
              {switching ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : <Network className="mr-1 size-3.5" />}
              Switch to {switchTo.label}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

/** The collapsed networks line: "eth0 10.2.10.40/24 (internet) · wlan0 10.2.10.41/24". */
function networksSummary(thing: Thing): string {
  const nets = readBirth(thing).networks;
  if (nets === null) return "not reported by this firmware";
  if (nets.length === 0) return "none reported";
  return nets
    .map((n) => [n.iface, n.cidrs[0], n.default_route ? "(internet)" : null, n.up ? null : "(down)"].filter(Boolean).join(" "))
    .join(" · ");
}

/** The read-only Networks panel: one row per interface. */
export function NetworksPanel({ thing, updated }: { thing: Thing; updated?: string | null }) {
  const nets = readBirth(thing).networks;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        <Network className="size-3.5" /> Networks
        {updated && <span className="font-normal normal-case tracking-normal">· updated {updated}</span>}
      </span>
      {nets === null ? (
        <span className="text-[11px] text-on-surface-muted">
          This firmware does not report its networks. Scans use a typed range or the gateway&apos;s own network.
        </span>
      ) : nets.length === 0 ? (
        <span className="text-[11px] text-on-surface-muted">The gateway reported no network interfaces.</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {nets.map((n) => (
            <li key={n.iface} className="flex flex-col gap-0.5 rounded border border-border/60 px-2 py-1 text-xs">
              <span className="flex flex-wrap items-center gap-1.5">
                {n.kind === "wifi" ? <Wifi className="size-3.5 text-on-surface-muted" /> : <Network className="size-3.5 text-on-surface-muted" />}
                <span className="font-mono font-medium text-on-surface">{n.iface}</span>
                <Badge variant={n.up ? "success" : "secondary"} className="text-[9px]">
                  {n.up ? "up" : "down"}
                </Badge>
                {n.default_route && (
                  <Badge variant="info" className="text-[9px]">
                    <Globe className="mr-0.5 size-2.5" /> internet
                  </Badge>
                )}
                {n.kind === "wifi" && n.ssid && <span className="text-on-surface-muted">Wi-Fi {n.ssid}</span>}
              </span>
              <span className="font-mono text-[11px] text-on-surface-muted">
                {n.cidrs.length > 0 ? n.cidrs.join(", ") : "no address"}
                {n.gateway ? ` · router ${n.gateway}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      <span className="text-[10px] text-on-surface-muted">
        Read-only. Change the gateway&apos;s network from the Sahas Forge phone app over Bluetooth, which puts the old
        settings back if the gateway does not come back online.
      </span>
    </div>
  );
}
